import {all, call, fork, put, takeEvery} from "redux-saga/effects";
import {
    SEARCH_ROLES,
    SEARCH_ROLES_SUCCESS,
    SEARCH_ROLES_FAILED,
    VIEW_ROLES,
    VIEW_ROLES_SUCCESS,
    VIEW_ROLES_FAILED,
    UPDATE_ROLES,
    UPDATE_ROLES_SUCCESS,
    UPDATE_ROLES_FAILED,
    CREATE_ROLES,
    CREATE_ROLES_SUCCESS,
    CREATE_ROLES_FAILED,
    DELETE_ROLES,
    DELETE_ROLES_SUCCESS,
    DELETE_ROLES_FAILED,
    RESET_STATUS,
} from "constants/ActionTypes";
import {
    searchRolesApi,
    viewRolesApi,
    updateRolesApi,
    createRolesApi,
    deleteRolesApi,
    getListPrivilegesApi,
} from "../../appRedux/api/Roles";
import {
    GET_LIST_PRIVILEGES,
    GET_LIST_PRIVILEGES_FAILED,
    GET_LIST_PRIVILEGES_SUCCESS,
} from "../../constants/ActionTypes";
import { mapRoleRow, mapRoleDetail, mapPagingToRecordInfo } from "../../appRedux/api/authHelpers";
import { apiHandler, AUTH_MUTATION_SUCCESS } from "./handler";

function* fetchAllRolesGenerator(payload) {
    const base = {
        ...payload,
        authToken: payload.authToken || localStorage.getItem("a"),
        deviceId: payload.deviceId || payload.deviceUniqueId || localStorage.getItem("d"),
        deviceUniqueId: payload.deviceUniqueId || payload.deviceId || localStorage.getItem("d"),
        size: payload.size || 1000,
    };
    const first = yield* apiHandler(searchRolesApi, { ...base, page: 0 });
    if (!first.ok) {
        return { ok: false, message: first.message, combined: [] };
    }
    const firstData = first.data || {};
    let combined = Array.isArray(firstData.body) ? firstData.body.slice() : [];
    const paging = firstData.paging;
    const total = paging && paging.totalElements != null ? Number(paging.totalElements) : combined.length;
    const actualPageSize = (paging && paging.pageSize != null) ? Number(paging.pageSize) : (base.size || 1000);

    let page = 1;
    while (combined.length < total && page < 50) {
        const next = yield* apiHandler(searchRolesApi, { ...base, page });
        if (!next.ok) break;
        const nextData = next.data || {};
        const rows = Array.isArray(nextData.body) ? nextData.body : [];
        if (rows.length === 0) break;
        combined = combined.concat(rows);
        const nextPageSize = (nextData.paging && nextData.paging.pageSize != null)
            ? Number(nextData.paging.pageSize)
            : actualPageSize;
        if (rows.length < nextPageSize) break;
        page += 1;
    }

    const seen = {};
    const uniqueCombined = combined.filter((item) => {
        const key = item && (item.id || item.roleId || item.code);
        if (!key) return true;
        if (seen[key]) return false;
        seen[key] = true;
        return true;
    });

    return { ok: true, combined: uniqueCombined, paging };
}

export function* fetchSearchRoles(action = {}) {
    const payload = action.payload || action;
    const isPaginated = payload && payload.size != null && !payload.fetchAll;

    if (!isPaginated) {
        const result = yield* fetchAllRolesGenerator(payload || {});
        if (result.ok) {
            const rows = Array.isArray(result.combined) ? result.combined.map(mapRoleRow) : [];
            const recordInfo = mapPagingToRecordInfo(result.paging);
            yield put({
                type: SEARCH_ROLES_SUCCESS,
                payload: {
                    data: rows,
                    recordInfo: {
                        ...recordInfo,
                        totalRecords: rows.length,
                    },
                },
            });
        } else {
            yield put({
                type: SEARCH_ROLES_FAILED,
                payload: result.message,
            });
        }
        return;
    }

    const { ok, data, message } = yield* apiHandler(searchRolesApi, payload);

    if (ok) {
        const rows = Array.isArray(data.body) ? data.body.map(mapRoleRow) : [];
        const recordInfo = mapPagingToRecordInfo(data.paging);

        yield put({
            type: SEARCH_ROLES_SUCCESS,
            payload: {
                data: rows,
                recordInfo,
            },
        });
    } else {
        yield put({
            type: SEARCH_ROLES_FAILED,
            payload: message,
        });
    }
}

function* fetchViewRoles({payload}) {
    if(payload != null){
        const { ok, data, message } = yield* apiHandler(viewRolesApi, payload);

        if (ok) {
            const detail = mapRoleDetail(data.body);
            yield put({type: VIEW_ROLES_SUCCESS, payload: { data: detail }});
        } else {
            yield put({type: VIEW_ROLES_FAILED, payload: message});
        }
    }
}

function* postEditRoles({payload}) {
    if(payload != null){
        yield put({type: RESET_STATUS});
        const resolvedPayload = {
            ...payload,
            deviceId: payload.deviceId || payload.deviceUniqueId,
        };
        const { ok, data, message } = yield* apiHandler(updateRolesApi, resolvedPayload, {
            successCodes: AUTH_MUTATION_SUCCESS,
        });

        if (ok) {
            yield put({type: UPDATE_ROLES_SUCCESS, payload: data.body || data.data});
        } else {
            yield put({type: UPDATE_ROLES_FAILED, payload: message});
        }
    }
}

function* postInsertRoles({payload}) {
    if(payload != null){
        yield put({type: RESET_STATUS});
        const resolvedPayload = {
            ...payload,
            deviceId: payload.deviceId || payload.deviceUniqueId,
        };
        const { ok, data, message } = yield* apiHandler(createRolesApi, resolvedPayload, {
            successCodes: AUTH_MUTATION_SUCCESS,
        });

        if (ok) {
            yield put({type: CREATE_ROLES_SUCCESS, payload: { data: data.body, ...data }});
        } else {
            yield put({type: CREATE_ROLES_FAILED, payload: message});
        }
    }
}

function* fetchAllPrivilegesGenerator(payload) {
    const base = {
        ...payload,
        deviceId: payload.deviceId || payload.deviceUniqueId,
        size: 100,
    };
    const first = yield* apiHandler(getListPrivilegesApi, { ...base, page: 0 });
    if (!first.ok) {
        return { ok: false, message: first.message };
    }
    const firstData = first.data;
    let combined = Array.isArray(firstData.body) ? firstData.body.slice() : [];
    const paging = firstData.paging;
    const total = paging && paging.totalElements != null ? paging.totalElements : combined.length;
    if (total > combined.length) {
        const second = yield* apiHandler(getListPrivilegesApi, { ...base, page: 1 });
        if (second.ok && Array.isArray(second.data.body)) {
            combined = combined.concat(second.data.body);
        }
    }
    return { ok: true, combined };
}

function* fetchListPrivileges({payload}) {
    const resolvedPayload = payload || {
        authToken: localStorage.getItem("a"),
        deviceUniqueId: localStorage.getItem("d"),
        deviceId: localStorage.getItem("d"),
    };

    if (resolvedPayload.authToken != null) {
        const result = yield* fetchAllPrivilegesGenerator(resolvedPayload);
        if (result.ok) {
            yield put({
                type: GET_LIST_PRIVILEGES_SUCCESS,
                payload: { data: { content: result.combined } },
            });
        } else {
            yield put({ type: GET_LIST_PRIVILEGES_FAILED, payload: result.message });
        }
    }
}

function* postDeleteRoles({payload}) {
    if(payload != null){
        const { ok, data, message } = yield* apiHandler(deleteRolesApi, payload, {
            successCodes: AUTH_MUTATION_SUCCESS,
        });

        if (ok) {
            yield put({type: DELETE_ROLES_SUCCESS, payload: data.body || data.data});
        } else {
            yield put({type: DELETE_ROLES_FAILED, payload: message});
        }
    }
}

export function* searchRoles() {
    yield takeEvery(SEARCH_ROLES, fetchSearchRoles);
}

export function* viewRoles(){
    yield takeEvery(VIEW_ROLES, fetchViewRoles);
}

export function* getListPrivileges(){
    yield takeEvery(GET_LIST_PRIVILEGES, fetchListPrivileges);
}

export function* editRoles(){
    yield takeEvery(UPDATE_ROLES, postEditRoles);
}

export function* createRoles(){
    yield takeEvery(CREATE_ROLES, postInsertRoles);
}

export function* removeRoles(){
    yield takeEvery(DELETE_ROLES, postDeleteRoles);
}

export default function* rootSaga() {
    yield all([
        fork(searchRoles),
        fork(viewRoles),
        fork(getListPrivileges),
        fork(editRoles),
        fork(createRoles),
        fork(removeRoles),
    ]);
}
